import React, { useState } from 'react';
import styles from './styles/ScenarioInviteModal.module.css';
import * as ScenarioAPI from '../../../services/ScenarioServices';
import { searchUsersByUsername } from '../../../services/UserServices';
import profileIcon from "../../../images/profileicon.png";

// The one message that means the list actually changed. Anything else comes back
// with a 200 too — the cap, an existing collaborator, the creator themselves —
// so treating any 2xx as success is what made the modal close on a no-op.
const ADDED_MESSAGE = 'Successfully added collaborator';

const ScenarioInviteModal = ({
    isOpen,
    onClose,
    scenarioId,
    currentCollaborators,
    setCollaborators,
    creatorId,
    cap = 2,
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [error, setError] = useState(null);
    const [hasSearched, setHasSearched] = useState(false);
    const [busy, setBusy] = useState(false);
    const [closing, setClosing] = useState(false);

    const collaborators = currentCollaborators || [];
    const remainingSlots = Math.max(0, cap - collaborators.length);

    const triggerClose = () => {
        setClosing(true);
        setTimeout(() => {
            handleClose();
            setClosing(false);
        }, 300);
    };

    const handleClose = () => {
        setSelectedUsers([]);
        setSearchResults([]);
        setSearchTerm('');
        setHasSearched(false);
        setError(null);
        onClose();
    };

    if (!isOpen && !closing) return null;

    // Anyone who already has edit access is filtered out rather than shown and
    // then refused: the creator cannot be their own collaborator, and re-adding
    // an existing one is a no-op.
    const alreadyHasAccess = (userId) =>
        String(userId) === String(creatorId) ||
        collaborators.some((collab) => String(collab._id || collab) === String(userId));

    const searchUser = async () => {
        try {
            const users = await searchUsersByUsername(searchTerm.trim());
            setSearchResults(users.filter((user) => !alreadyHasAccess(user._id)));
            setError(null);
            setHasSearched(true);
        } catch (err) {
            setError(err.response?.data?.message || 'An error occurred during search.');
            setHasSearched(true);
        }
    };

    const toggleSelectUser = (user) => {
        setSelectedUsers((prev) =>
            prev.some((selected) => selected._id === user._id)
                ? prev.filter((selected) => selected._id !== user._id)
                : [...prev, user]
        );
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && searchTerm.trim()) {
            searchUser();
        }
    };

    /**
     * Invites are sent one at a time because the cap is enforced per request:
     * sending both halves of a two-person selection in parallel could both pass
     * a check that only one of them should. The last response carries the list
     * the server ended up with, which is what the page renders.
     */
    const inviteUsers = async (users) => {
        if (!users.length || busy) return;

        setBusy(true);
        setError(null);
        try {
            let latest = null;
            const declined = [];

            for (const user of users) {
                const result = await ScenarioAPI.addCollaborator(scenarioId, user._id);
                if (result?.collaborators) latest = result.collaborators;
                if (result?.message !== ADDED_MESSAGE) {
                    declined.push(`${user.username}: ${result?.message || 'not added'}`);
                }
            }

            if (latest) setCollaborators(latest);

            if (declined.length) {
                // Stay open and say why. Closing here is what made a rejected
                // invite look identical to an accepted one.
                setError(declined.join('; '));
                setSelectedUsers([]);
                setSearchResults((prev) => prev.filter((user) => !alreadyHasAccess(user._id)));
                return;
            }

            triggerClose();
        } catch (err) {
            setError(
                err.response?.status === 403
                    ? 'Only the scenario creator can add collaborators.'
                    : err.response?.data?.message || 'Could not add that collaborator.'
            );
        } finally {
            setBusy(false);
        }
    };

    const tooManySelected = selectedUsers.length > remainingSlots;

    return (
        <div className={`${styles.modalOverlay} ${closing ? styles.fadeOut : styles.fadeIn}`}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <h2 className={styles.modalTitle}>Invite Collaborators</h2>
                </div>

                {/* Modal Body */}
                <div className={styles.modalBody}>

                    {/* Search and Invite by Username */}
                    <div className={styles.searchContainer}>
                        <input
                            type="text"
                            placeholder="Search users by username"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className={styles.searchInput}
                        />
                        <button
                            type="button"
                            onClick={searchUser}
                            className={`${styles.buttonBase} ${styles.searchButton}`}
                            disabled={!searchTerm.trim()}
                        >
                            Search
                        </button>
                    </div>
                    {error && <div className={styles.error}>{error}</div>}
                    {remainingSlots === 0 && (
                        <div className={styles.error}>
                            This scenario already has the maximum of {cap} collaborators.
                            Remove one before inviting somebody else.
                        </div>
                    )}
                    {tooManySelected && (
                        <div className={styles.error}>
                            Only {remainingSlots} more {remainingSlots === 1 ? 'collaborator' : 'collaborators'} can be added.
                        </div>
                    )}
                    {hasSearched && searchResults.length === 0 && !error && (
                        <div className={styles.noResults}>No users found.</div>
                    )}
                    <div className={styles.resultsContainer}>
                        {searchResults.map(user => (
                            <div
                            key={user._id}
                            className={`${styles.userItem} ${
                                selectedUsers.some(selectedUser => selectedUser._id === user._id)
                                ? styles.selectedUser
                                : ''
                            }`}
                            onClick={() => toggleSelectUser(user)}
                            >
                            <img src={user.profilePicture || profileIcon} alt="" className={styles.profileIcon} />
                            <span className={styles.userInfo}>{user.username}</span>
                            <button
                                type="button"
                                className={styles.inviteButton}
                                disabled={busy || remainingSlots === 0}
                                onClick={(e) => {
                                e.stopPropagation();
                                inviteUsers([user]);
                                }}
                            >
                                Invite
                            </button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className={styles.modalFooter}>
                    <div className={styles.buttonGroup}>
                        <button
                            type="button"
                            onClick={() => inviteUsers(selectedUsers)}
                            className={`${styles.buttonBase} ${styles.submitButton}`}
                            disabled={busy || selectedUsers.length === 0 || tooManySelected}
                        >
                            {busy
                                ? 'Inviting…'
                                : `Invite selected${selectedUsers.length ? ` (${selectedUsers.length})` : ''}`}
                        </button>
                        <button type="button" onClick={triggerClose} className={`${styles.buttonBase} ${styles.cancelButton}`}>
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ScenarioInviteModal;
